import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'antd';

const measureTextWidth = (text, cellElement) => {
    const span = document.createElement('span');
    span.style.visibility = 'hidden';
    span.style.position = 'absolute';
    span.style.whiteSpace = 'nowrap';
    const style = window.getComputedStyle(cellElement);
    span.style.font = style.font;
    span.style.padding = style.padding;
    span.textContent = text;
    document.body.appendChild(span);
    const width = span.offsetWidth;
    document.body.removeChild(span);
    return width + 6;
};

const TableTooltipModal = () => {
    const [modalVisible, setModalVisible] = useState(false);
    const [modalContent, setModalContent] = useState('');
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const [tooltipContent, setTooltipContent] = useState('');
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

    const tooltipTimer = useRef(null);
    const clickTimer = useRef(null);
    const tooltipRef = useRef(null);

    useEffect(() => {
        // --- 1. 创建 Tooltip DOM ---
        const tooltipDiv = document.createElement('div');
        tooltipDiv.id = 'global-table-tooltip';
        tooltipDiv.style.cssText = `
            position: fixed;
            z-index: 9999;
            background: #fff;
            padding: 8px 12px;
            border: 1px solid #d9d9d9;
            border-radius: 4px;
            box-shadow: 0 2px 8px rgba(0,0,0,0.15);
            display: none;
            max-width: 400px;
            word-wrap: break-word;
            pointer-events: none;
            font-size: 12px;
            color: #333;
            box-sizing: border-box;
        `;
        document.body.appendChild(tooltipDiv);
        tooltipRef.current = tooltipDiv;

        // --- 2. 核心修复：获取有效 DOM 元素 ---
        const getTargetElement = (node) => {
            if (node instanceof Element) return node;
            if (node && (node.nodeType === Node.TEXT_NODE || node.nodeType === Node.COMMENT_NODE)) {
                return node.parentElement || node.parentNode;
            }
            return null;
        };

        // --- 3. 辅助函数：判断元素是否为“可点击的交互元素” ---
        // 如果是这些元素，点击时不应触发 Modal
        const isInteractiveElement = (element) => {
            if (!element || !element.tagName) return false;

            const tagName = element.tagName.toLowerCase();
            const className = element.getAttribute('class') || '';;

            // 检查标签名
            if (['A', 'BUTTON', 'INPUT', 'SELECT', 'TEXTAREA', 'IMG', 'Image'].includes(tagName.toUpperCase())) {
                return true;
            }

            // 检查类名 (AntD 的按钮和链接通常包含这些类)
            // 也可以检查是否有 onclick 属性，但类名更直观
            if (className.includes('ant-btn') || className.includes('ant-link') || className.includes('ant-checkbox') || className.includes('ant-radio')) {
                return true;
            }

            // 检查是否有手型光标的内联样式或 computedStyle
            const style = window.getComputedStyle(element);
            if (style && style.cursor === 'pointer') {
                return true;
            }

            return false;
        };

        // --- 4. 鼠标移入处理 ---
        const handleMouseOver = (e) => {
            const el = getTargetElement(e.target);
            if (!el) return;

            const cell = el.closest('.ant-table-cell');
            if (!cell) return;

            const content = cell.textContent?.trim() || '';
            const isEllipsis = cell.offsetWidth < measureTextWidth(content, cell);

            if (!content || !isEllipsis) return;

            setTooltipContent(content);
            setModalContent(content);

            tooltipTimer.current = setTimeout(() => {
                setTooltipVisible(true);
                setTooltipPosition({ x: e.clientX + 10, y: e.clientY + 10 });
            }, 500);
        };

        // --- 5. 鼠标移出处理 ---
        const handleMouseOut = (e) => {
            clearTimeout(tooltipTimer.current);
            setTooltipVisible(false);
        };

        // --- 6. 点击处理 (优化版) ---
        const handleClick = (e) => {
            const el = getTargetElement(e.target);
            if (!el) return;

            // --- 核心新增逻辑：检查点击的目标是否是交互元素 ---
            // 从点击的元素向上遍历，检查是否在表格单元格内点击了按钮或链接
            let current = el;
            // 防止无限循环
            while (current && current !== document) {
                // 如果找到了表格单元格，说明已经检查完单元格内的所有元素，可以跳出
                if (current.classList?.contains('ant-table-cell')) {
                    break;
                }
                // 如果在到达单元格之前发现了交互元素，则阻止 Modal 弹出
                if (isInteractiveElement(current)) {
                    return; // 直接返回，不执行后续逻辑
                }
                current = current.parentElement;
            }

            // 查找最近的表格单元格
            const cell = el.closest('.ant-table-cell');
            if (!cell) return;

            const content = cell.textContent?.trim() || '';
            const isEllipsis = cell.offsetWidth < measureTextWidth(content, cell);
            if (!content || !isEllipsis) return;

            if (clickTimer.current) {
                clearTimeout(clickTimer.current);
                clickTimer.current = null;
            }
            clickTimer.current = setTimeout(() => {
                setModalVisible(true);
                setModalContent(content);
            }, 250);
        };
        const handleDoubleClick = (e) => {
            if (clickTimer.current) {
                clearTimeout(clickTimer.current);
                clickTimer.current = null;
            }
            const event = new CustomEvent('tableCellDoubleClicked', { detail: { clientX: e.clientX, clientY: e.clientY } });
            document.dispatchEvent(event);
        };

        // --- 7. 绑定事件 ---
        document.addEventListener('mouseover', handleMouseOver, true);
        document.addEventListener('mouseout', handleMouseOut);
        document.addEventListener('click', handleClick);
        document.addEventListener('dblclick', handleDoubleClick);

        return () => {
            clearTimeout(tooltipTimer.current);
            if (clickTimer.current) clearTimeout(clickTimer.current);
            document.removeEventListener('mouseover', handleMouseOver, true);
            document.removeEventListener('mouseout', handleMouseOut);
            document.removeEventListener('click', handleClick);
            document.removeEventListener('dblclick', handleDoubleClick);
            
            if (tooltipRef.current && tooltipRef.current.parentNode) {
                tooltipRef.current.parentNode.removeChild(tooltipRef.current);
            }
        };
    }, []); // 依赖数组保持为空

    // --- 8. 同步 Tooltip 显示 ---
    useEffect(() => {
        if (!tooltipRef.current) return;
        if (tooltipVisible && tooltipContent) {
            tooltipRef.current.textContent = tooltipContent;
            tooltipRef.current.style.left = `${tooltipPosition.x}px`;
            tooltipRef.current.style.top = `${tooltipPosition.y}px`;
            tooltipRef.current.style.display = 'block';
        } else {
            tooltipRef.current.style.display = 'none';
        }
    }, [tooltipVisible, tooltipContent, tooltipPosition]);

    return (
        <Modal
            title="内容详情"
            open={modalVisible}
            onCancel={() => setModalVisible(false)}
            footer={null}
            width={600}
        >
            <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {modalContent}
            </div>
        </Modal>
    );
};

export default TableTooltipModal;